import "zod/compile";
import { z } from "zod";
import { QueryIRSchema } from "./queryBuilder";
import { ReportCanvasLayoutSchema } from "./reportCanvas";
import { normalizeReportCanvasLayout } from "./reportComponentShape";
import {
  WORKBOOK_MAX_VALIDATIONS,
  WORKBOOK_VALIDATION_SAMPLE_ROWS,
  workbookSampleSchema,
  workbookValidationSchema,
} from "./workbookValidations";
import { reportFiltersSchema } from "./reportFilters";
import {
  workbookResultTotalsSchema,
  workbookRunComparisonSchema,
  workbookRunSamplingSchema,
  workbookSamplingSchema,
} from "./workbookSampling";

/**
 * The most rows a table held inline (in a Definition, a request or a stored
 * JSON object) may have. A table past it is a Large table: a Run captures and
 * stores it as a Table file instead, and grids show a Sample of it.
 */
export const WORKBOOK_MAX_ROWS = 100_000;
/** The most rows any table a Run reads or computes may have. */
export const WORKBOOK_MAX_RUN_ROWS = 20_000_000;
export const WORKBOOK_MAX_COLUMNS = 100;
export const WORKBOOK_PREVIEW_ROWS = 100;
/** Preview cells longer than this are cut and end in an ellipsis. */
export const WORKBOOK_PREVIEW_CELL_LENGTH = 500;
export const WORKBOOK_MAX_BYTES = 32 * 1024 * 1024;
/** How long each of a Run's own Compute requests (capture, transform, large downloads) may take. */
export const WORKBOOK_RUN_COMPUTE_TIMEOUT_MS = 20 * 60_000;
/**
 * How long a workflow step executing a whole Run may take: its Compute limit
 * plus room to capture Sources, store Results and export Files. The Run
 * Workbook node and the Backing workflow's step both use it.
 */
export const WORKBOOK_RUN_STEP_TIMEOUT_MS =
  WORKBOOK_RUN_COMPUTE_TIMEOUT_MS + 10 * 60_000;
/** {@link WORKBOOK_RUN_STEP_TIMEOUT_MS} as a workflow step's duration. */
export const WORKBOOK_RUN_STEP_TIMEOUT = `${WORKBOOK_RUN_STEP_TIMEOUT_MS / 60_000} minutes`;
/** The most values a Value list filter holds. */
export const WORKBOOK_MAX_FILTER_VALUES = 1000;
/** The most Distinct values a Result records per Field; more marks the Field truncated. */
export const WORKBOOK_MAX_DISTINCT_VALUES = 1000;

/**
 * The spreadsheet error values a cell can hold instead of a value. The engine
 * produces #N/A (a Lookup with no match), #VALUE! (a value that isn't its
 * Declared type) and #DIV/0!; the rest arrive only from an xlsx File's cells.
 */
export const WORKBOOK_ERROR_CODES = [
  "#N/A",
  "#VALUE!",
  "#DIV/0!",
  "#REF!",
  "#NAME?",
  "#NUM!",
  "#NULL!",
] as const;
export const workbookErrorCodeSchema = z.enum(WORKBOOK_ERROR_CODES);
export type WorkbookErrorCode = z.infer<typeof workbookErrorCodeSchema>;
/** An Error cell: an error value in place of a value, with why it happened, e.g. "'abc' isn't a Number". */
export const workbookErrorCellSchema = z.object({
  error: workbookErrorCodeSchema,
  cause: z.string().max(1000).optional(),
});
export type WorkbookErrorCell = z.infer<typeof workbookErrorCellSchema>;
export const workbookCellSchema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.null(),
  workbookErrorCellSchema,
]);
/** Whether a cell holds an error value. */
export function isWorkbookErrorCell(cell: unknown): cell is WorkbookErrorCell {
  return typeof cell === "object" && cell !== null && "error" in cell;
}
export const workbookTableSchema = z
  .object({
    columns: z
      .array(z.string().min(1).max(200))
      .min(1)
      .max(WORKBOOK_MAX_COLUMNS),
    rows: z.array(z.array(workbookCellSchema)).max(WORKBOOK_MAX_ROWS),
  })
  .superRefine((table, context) => {
    if (
      new Set(table.columns.map((name) => name.toLowerCase())).size !==
      table.columns.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Column names must be unique, ignoring case.",
      });
    }
    if (table.rows.some((row) => row.length !== table.columns.length)) {
      context.addIssue({
        code: "custom",
        message: "Every row must match the table's columns.",
      });
    }
  });
export type WorkbookTable = z.infer<typeof workbookTableSchema>;
export type WorkbookCell = z.infer<typeof workbookCellSchema>;

/** A Worksheet column's Declared type. */
export const workbookColumnTypeSchema = z.enum([
  "text",
  "decimal",
  "date",
  "datetime",
  "boolean",
]);
export type WorkbookColumnType = z.infer<typeof workbookColumnTypeSchema>;

/** The comparisons an Entity Source's filters make, pushed down to the database. */
export const WORKBOOK_ENTITY_FILTER_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "is-null",
  "is-not-null",
] as const;
/**
 * A row filter an Entity Source applies before its rows are read: `column` names one
 * of the Entity's columns, compared as that column's type (`contains` as text).
 */
export const workbookEntityFilterSchema = z.object({
  column: z.string().min(1).max(200),
  operator: z.enum(WORKBOOK_ENTITY_FILTER_OPERATORS),
  value: z.string().max(1000).default(""),
});
export type WorkbookEntityFilter = z.infer<typeof workbookEntityFilterSchema>;
/** The most filters an Entity Source holds. */
export const WORKBOOK_MAX_ENTITY_FILTERS = 20;

/** How a filter value reads as each typed column's type. */
const ENTITY_FILTER_VALUE_READS: Record<
  Exclude<WorkbookColumnType, "text">,
  (value: string) => boolean
> = {
  decimal: (value) => /^-?\d+(?:\.\d+)?$/.test(value),
  date: (value) =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)),
  datetime: (value) =>
    /^\d{4}-\d{2}-\d{2}/.test(value) && !Number.isNaN(Date.parse(value)),
  boolean: (value) => value === "true" || value === "false",
};
/**
 * Why an Entity filter can't be applied to a column of `type`, or null: its value
 * isn't the column's type (`not-type`), or it orders a true/false column
 * (`boolean-comparison`). Browser and server check the same rule, so a filter the
 * editor accepts is one the server runs.
 */
export function workbookEntityFilterProblem(
  filter: Pick<WorkbookEntityFilter, "operator" | "value">,
  type: WorkbookColumnType,
): "not-type" | "boolean-comparison" | null {
  if (
    type === "text" ||
    filter.operator === "is-null" ||
    filter.operator === "is-not-null" ||
    filter.operator === "contains"
  )
    return null;
  if (!ENTITY_FILTER_VALUE_READS[type](filter.value.trim())) return "not-type";
  if (
    type === "boolean" &&
    filter.operator !== "eq" &&
    filter.operator !== "neq"
  )
    return "boolean-comparison";
  return null;
}

export const WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS = 2;
export const WORKBOOK_MAX_SCHEMA_GROUP_MEMBERS = 20;
const WORKBOOK_MAX_SCHEMA_GROUP_EXCLUDED = 500;

const isUnique = (ids: readonly string[]) => new Set(ids).size === ids.length;
/**
 * A UUID in canonical (lowercase) form: Postgres hands ids back lowercase and
 * the readers compare them as strings, so a definition stores only that form.
 */
const canonicalUuid = z.uuid().overwrite((id) => id.toLowerCase());

/** Which of a Schema Definition's Data Sources a group reads. */
export const workbookSchemaGroupMembersSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("all"),
    exclude: z
      .array(canonicalUuid)
      .max(WORKBOOK_MAX_SCHEMA_GROUP_EXCLUDED)
      .refine(isUnique, {
        message: "Each excluded Data Source may appear once",
      })
      .optional(),
  }),
  z.object({
    mode: z.literal("pick"),
    linkIds: z
      .array(canonicalUuid)
      .min(WORKBOOK_MIN_SCHEMA_GROUP_MEMBERS)
      .max(WORKBOOK_MAX_SCHEMA_GROUP_MEMBERS)
      .refine(isUnique, { message: "Each picked Data Source may appear once" }),
  }),
]);
export type WorkbookSchemaGroupMembers = z.infer<
  typeof workbookSchemaGroupMembersSchema
>;

/**
 * The links a group reads, given `available`, the ids of the schema's links that
 * can be read: all of them less the excluded ("all"), or the picked ones in their
 * order ("pick", not checked against `available`: the reader reports a missing one).
 */
export function workbookSchemaGroupLinkIds(
  members: WorkbookSchemaGroupMembers,
  available: readonly string[],
): string[] {
  if (members.mode === "pick") return members.linkIds;
  const excluded = new Set(members.exclude);
  return available.filter((id) => !excluded.has(id));
}

export const WORKBOOK_ITEM_NAME_MAX_LENGTH = 200;
const item = {
  id: z.uuid(),
  name: z.string().trim().min(1).max(WORKBOOK_ITEM_NAME_MAX_LENGTH),
  folder: z.string().max(200).default(""),
};
export const workbookSourceSchema = z.discriminatedUnion("kind", [
  z.object({
    ...item,
    kind: z.literal("forecast-snapshots"),
    runId: z.uuid().optional(),
    asOfFrom: z.iso.date().optional(),
    asOfThrough: z.iso.date().optional(),
  }),
  z.object({
    ...item,
    kind: z.literal("file"),
    fileId: z.uuid(),
    sheetName: z.string(),
    headerRow: z.number().int().min(1).max(10000),
  }),
  z.object({ ...item, kind: z.literal("data-source"), linkId: z.uuid() }),
  /**
   * The Data Sources (links) of one Schema Definition, combined (UNION ALL) into
   * one table. Because they share the schema, their columns match. `members`
   * says which: every readable link of the schema less `exclude` ("all", the
   * default, resolved at read time so a feed added to the schema joins on its
   * own), or a fixed list ("pick"). Members are read ordered by name. A member's
   * label, shown in the `labelColumn` when set, is its name unless `labels`
   * gives it another.
   */
  z.object({
    ...item,
    kind: z.literal("schema-group"),
    schemaId: canonicalUuid,
    members: workbookSchemaGroupMembersSchema,
    labels: z
      .record(canonicalUuid, z.string().trim().min(1).max(200))
      .optional(),
    labelColumn: z.string().trim().min(1).max(200).optional(),
  }),
  z.object({ ...item, kind: z.literal("mapping"), mappingId: z.uuid() }),
  /** A Stateful table's current rows, read directly (no Data Mapping). */
  z.object({
    ...item,
    kind: z.literal("stateful-table"),
    tableId: z.uuid(),
  }),
  /** A Data Asset: its Latest version unless `versionId` pins one. */
  z.object({
    ...item,
    kind: z.literal("data-asset"),
    dataAssetId: z.uuid(),
    versionId: z.uuid().optional(),
  }),
  z.object({ ...item, kind: z.literal("object"), query: QueryIRSchema }),
  z.object({ ...item, kind: z.literal("input"), table: workbookTableSchema }),
  /**
   * An Entity: a Gently entity that supports Schema Extensions (`entity` is its
   * extension target, e.g. `IM_PRODUCT_V3`), read with its extension fields and
   * narrowed by `filters`, all of which a row must pass.
   */
  z.object({
    ...item,
    kind: z.literal("entity"),
    entity: z.string().min(1).max(100),
    filters: z
      .array(workbookEntityFilterSchema)
      .max(WORKBOOK_MAX_ENTITY_FILTERS)
      .optional(),
  }),
]);
export type WorkbookSource = z.infer<typeof workbookSourceSchema>;
/**
 * A Data Source link, Data Mapping, Stateful table, Data Asset or Entity the user can Read, offered
 * as a Source. An Entity's `id` is its extension target, the others' a uuid.
 */
export const workbookCatalogEntrySchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  /** Human-readable source metadata; name remains the raw table identifier. */
  label: z.string().optional(),
  description: z.string().optional(),
  kind: z.enum([
    "data-source",
    "mapping",
    "stateful-table",
    "data-asset",
    "entity",
    "forecast-snapshots",
  ]),
  /**
   * A Data Source link's Schema Definition: the links sharing a `schemaId` have
   * the same columns and can be combined into one Source.
   */
  schemaId: z.uuid().optional(),
  schemaName: z.string().optional(),
  /** A Data Asset's uploaded versions, newest first. */
  versions: z
    .array(z.object({ id: z.uuid(), versionNumber: z.number().int() }))
    .optional(),
  /** A Data Asset's Latest version: the one the asset currently serves. */
  latestVersionId: z.uuid().nullable().optional(),
  /**
   * An Entity's columns: its standard fields, then its extension fields. A Data
   * Source link's: its schema's fields, in field order, typed as the schema
   * declares them (the Source still reads every cell as text), with the label
   * and description the schema gives them.
   */
  columns: z
    .array(
      z.object({
        name: z.string(),
        type: workbookColumnTypeSchema,
        label: z.string().optional(),
        description: z.string().optional(),
      }),
    )
    .optional(),
});
export type WorkbookCatalogEntry = z.infer<typeof workbookCatalogEntrySchema>;

export const workbookColumnSchema = z.object({
  name: z.string().trim().min(1).max(200),
  expression: z.string().min(1).max(2000),
  type: workbookColumnTypeSchema.default("text"),
  numberFormat: z.string().max(100).optional(),
});
export const workbookFilterSchema = z.object({
  column: z.string().min(1),
  operator: z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "is-null",
    "is-not-null",
    // Error cells match only these; every other operator treats them as not matching.
    "is-error",
    "is-not-error",
    "is-na",
    // Value list filters: they read `values`, not `value`.
    "is-one-of",
    "is-not-one-of",
  ]),
  value: z.string().max(1000).default(""),
  /**
   * A Value list filter's values, as the Field's values read as text; "" is
   * the Empty entry, matching what Is Empty matches. Absent on other filters.
   */
  values: z
    .array(z.string().max(1000))
    .max(WORKBOOK_MAX_FILTER_VALUES)
    .optional(),
});
/** The operators of a Value list filter, which keep or remove the rows whose value is one of `values`. */
export const WORKBOOK_VALUE_LIST_OPERATORS = [
  "is-one-of",
  "is-not-one-of",
] as const;
export type WorkbookValueListOperator =
  (typeof WORKBOOK_VALUE_LIST_OPERATORS)[number];
/** Whether a filter operator is a Value list filter's. */
export function isWorkbookValueListOperator(
  operator: string,
): operator is WorkbookValueListOperator {
  return (WORKBOOK_VALUE_LIST_OPERATORS as readonly string[]).includes(
    operator,
  );
}
/**
 * A Lookup's "If not found": `na` gives the cell #N/A, `null` leaves it Empty
 * and `error` fails the Run.
 */
export const workbookLookupMissingSchema = z.enum(["na", "null", "error"]);
/** What every kind of Worksheet has on its Result, whatever computed it. */
const sheetResult = {
  /** Rules every row of the Result must pass, checked over all of it by each Run; absent is none. */
  validations: z
    .array(workbookValidationSchema)
    .max(WORKBOOK_MAX_VALIDATIONS)
    .optional(),
  /** How the grid samples a Large Result; absent is its first rows. Presentational. */
  sample: workbookSampleSchema.optional(),
};
/**
 * A columnar Worksheet: input columns plus Formula columns, row by row, then
 * filters and sort. A Worksheet without a `kind` is columnar, as every
 * Worksheet saved before sheet kinds existed is.
 */
export const workbookColumnarSheetSchema = z.object({
  ...item,
  kind: z.literal("columnar").optional(),
  inputId: z.uuid(),
  columns: z.array(workbookColumnSchema).min(1).max(WORKBOOK_MAX_COLUMNS),
  filters: z.array(workbookFilterSchema).max(30).default([]),
  lookups: z
    .array(
      z.object({
        sourceId: z.uuid(),
        inputColumn: z.string().min(1),
        matchColumn: z.string().min(1),
        valueColumn: z.string().min(1),
        outputColumn: z.string().min(1).max(200),
        /** A stored Lookup without it keeps its original meaning, Empty; new Lookups set `na`. */
        missing: workbookLookupMissingSchema.default("null"),
      }),
    )
    .max(10)
    .default([]),
  sort: z
    .array(
      z.object({
        column: z.string().min(1),
        direction: z.enum(["asc", "desc"]),
      }),
    )
    .max(10)
    .default([]),
  ...sheetResult,
});
export type WorkbookColumnarSheet = z.infer<typeof workbookColumnarSheetSchema>;
/** A columnar Worksheet's filter; a pivot's Filters use the same conditions. */
export type WorkbookFilter = z.infer<typeof workbookFilterSchema>;

/** How a pivot summarizes a Value field, named after the spreadsheet functions. */
export const WORKBOOK_PIVOT_SUMMARIES = [
  "sum",
  "count",
  "counta",
  "countunique",
  "average",
  "min",
  "max",
  "median",
] as const;
export const workbookPivotSummarySchema = z.enum(WORKBOOK_PIVOT_SUMMARIES);
export type WorkbookPivotSummary = z.infer<typeof workbookPivotSummarySchema>;
/** What a pivot's Value cell shows: the summary, or it as a fraction of a total. */
export const WORKBOOK_PIVOT_SHOW_AS = [
  "value",
  "percent-of-grand-total",
  "percent-of-row",
  "percent-of-column",
] as const;
export const workbookPivotShowAsSchema = z.enum(WORKBOOK_PIVOT_SHOW_AS);
export type WorkbookPivotShowAs = z.infer<typeof workbookPivotShowAsSchema>;
/** Date grouping: a Date field's values grouped by year, quarter, month or day. */
export const WORKBOOK_PIVOT_DATE_GROUPS = [
  "year",
  "quarter",
  "month",
  "day",
] as const;
export const workbookPivotDateGroupSchema = z.enum(WORKBOOK_PIVOT_DATE_GROUPS);
export type WorkbookPivotDateGroup = z.infer<
  typeof workbookPivotDateGroupSchema
>;
/**
 * A pivot Group field (a Row or Column field): an input Field whose distinct
 * values become rows or columns, sorted by their labels or by a Value's
 * summary (`sortBy.value` indexes `values`).
 */
export const workbookPivotGroupSchema = z.object({
  field: z.string().min(1).max(200),
  dateGroup: workbookPivotDateGroupSchema.optional(),
  sortBy: z
    .discriminatedUnion("by", [
      z.object({
        by: z.literal("label"),
        direction: z.enum(["asc", "desc"]).default("asc"),
      }),
      z.object({
        by: z.literal("value"),
        value: z.number().int().nonnegative(),
        direction: z.enum(["asc", "desc"]).default("desc"),
      }),
    ])
    .default({ by: "label", direction: "asc" }),
  /** A Row field's subtotal row after each of its groups. Ignored on the innermost Row field. */
  subtotals: z.boolean().default(false),
});
export type WorkbookPivotGroup = z.infer<typeof workbookPivotGroupSchema>;
/** A pivot Value: an input Field summarized per cell; `name` names its output column. */
export const workbookPivotValueSchema = z.object({
  field: z.string().min(1).max(200),
  summarize: workbookPivotSummarySchema.default("sum"),
  showAs: workbookPivotShowAsSchema.default("value"),
  /** Absent means the default name, such as "SUM of Net". */
  name: z.string().trim().min(1).max(200).optional(),
});
export type WorkbookPivotValue = z.infer<typeof workbookPivotValueSchema>;
/** The most Column keys a pivot spreads into columns. */
export const WORKBOOK_PIVOT_MAX_COLUMN_KEYS = 200;
export const workbookPivotSpecSchema = z.object({
  rows: z.array(workbookPivotGroupSchema).max(10).default([]),
  columns: z.array(workbookPivotGroupSchema).max(5).default([]),
  values: z.array(workbookPivotValueSchema).max(20).default([]),
  /** Conditions on input Fields, before summarizing: a row is summarized when it matches them all. */
  filters: z.array(workbookFilterSchema).max(30).default([]),
  /** A Grand Total row (`rows`) and, with Column fields, Grand Total columns (`columns`). */
  grandTotals: z
    .object({ rows: z.boolean(), columns: z.boolean() })
    .default({ rows: true, columns: true }),
  /** `hide` leaves out the rows whose Row or Column field is Empty. */
  emptyGroups: z.enum(["show", "hide"]).default("show"),
  /** What a Value cell with no rows behind it shows: Empty, or 0. */
  emptyCells: z.enum(["empty", "zero"]).default("empty"),
  /**
   * Pinned Column keys: the Column field labels (one per Column field) spread
   * into columns, in order, so the output columns never depend on data.
   * Absent: every key in the data, in the Column fields' sort order.
   */
  columnKeys: z
    .array(z.array(z.string().max(200)).min(1).max(5))
    .max(WORKBOOK_PIVOT_MAX_COLUMN_KEYS)
    .optional(),
});
export type WorkbookPivotSpec = z.infer<typeof workbookPivotSpecSchema>;
/**
 * A pivot Worksheet: its input's rows summarized by Row, Column and Value
 * fields after its Filters, as a spreadsheet pivot table summarizes them. Its
 * Result is a table like any Worksheet's (see `pivotOutputColumns`).
 */
export const workbookPivotSheetSchema = z.object({
  ...item,
  kind: z.literal("pivot"),
  inputId: z.uuid(),
  pivot: workbookPivotSpecSchema,
  ...sheetResult,
});
export type WorkbookPivotSheet = z.infer<typeof workbookPivotSheetSchema>;

/** A freeform sheet's grid: at most this many rows and columns (A to AZ), and filled cells. */
export const WORKBOOK_FREEFORM_MAX_ROWS = 1000;
export const WORKBOOK_FREEFORM_MAX_COLUMNS = 52;
export const WORKBOOK_FREEFORM_MAX_CELLS = 10000;
/** Whether an A1 address (`B3`) is inside the freeform grid, A1 to AZ1000. */
function inFreeformGrid(address: string): boolean {
  const match = /^([A-Z]{1,2})([1-9][0-9]{0,3})$/.exec(address);
  if (!match?.[1] || !match[2]) return false;
  const column = [...match[1]].reduce(
    (total, letter) => total * 26 + letter.charCodeAt(0) - 64,
    0,
  );
  return (
    column <= WORKBOOK_FREEFORM_MAX_COLUMNS &&
    Number(match[2]) <= WORKBOOK_FREEFORM_MAX_ROWS
  );
}
const GRID_BOUNDS = `Use a cell from A1 to AZ${WORKBOOK_FREEFORM_MAX_ROWS}.`;
/** A cell's A1 address: its column letters, then its row, 1-based, inside the grid. */
export const workbookCellAddressSchema = z
  .string()
  .regex(/^[A-Z]{1,2}[1-9][0-9]{0,3}$/, "Use an A1 cell address, such as B3.")
  .refine(inFreeformGrid, GRID_BOUNDS);
/** An A1 range, such as `A1:C20`: its top-left cell, then its bottom-right, both inside the grid. */
export const workbookCellRangeSchema = z
  .string()
  .regex(
    /^[A-Z]{1,2}[1-9][0-9]{0,3}:[A-Z]{1,2}[1-9][0-9]{0,3}$/,
    "Use an A1 range, such as A1:C20.",
  )
  .refine((range) => range.split(":").every(inFreeformGrid), GRID_BOUNDS);
/**
 * The table a freeform sheet's Result holds: a range whose first row is its
 * header (each cell literal text, naming a column) and whose other rows are
 * its rows. `columns` declares each column's type by position; a column past
 * them is Text.
 */
export const workbookFreeformTableSchema = z.object({
  range: workbookCellRangeSchema,
  columns: z
    .array(
      z.object({
        type: workbookColumnTypeSchema,
        numberFormat: z.string().max(100).optional(),
      }),
    )
    .max(WORKBOOK_FREEFORM_MAX_COLUMNS)
    .default([]),
});
export type WorkbookFreeformTable = z.infer<typeof workbookFreeformTableSchema>;
/**
 * A freeform sheet: a spreadsheet grid whose cells each hold what was typed
 * into them, a literal or a Formula (`=`), by A1 address. A Formula is an
 * Excel formula over this grid (`=A4*2`) and single cells or ranges of other
 * Sources and Worksheets (`='Prices'!B3`), stored by the table's id
 * (`{workbook:ID}!B3`). Its Result is the table `table` declares.
 */
export const workbookFreeformSheetSchema = z.object({
  ...item,
  kind: z.literal("freeform"),
  cells: z
    .record(workbookCellAddressSchema, z.string().max(8192))
    .refine(
      (cells) => Object.keys(cells).length <= WORKBOOK_FREEFORM_MAX_CELLS,
      `A freeform sheet holds at most ${WORKBOOK_FREEFORM_MAX_CELLS} filled cells.`,
    )
    .default({}),
  table: workbookFreeformTableSchema.optional(),
  ...sheetResult,
});
export type WorkbookFreeformSheet = z.infer<typeof workbookFreeformSheetSchema>;

/** A Worksheet of any kind. `kind` absent is columnar. */
export const workbookSheetSchema = z.union([
  workbookColumnarSheetSchema,
  workbookPivotSheetSchema,
  workbookFreeformSheetSchema,
]);
export type WorkbookSheet = z.infer<typeof workbookSheetSchema>;
export type WorkbookSheetKind = NonNullable<WorkbookSheet["kind"]>;
/** Whether a Worksheet is a pivot. */
export function isPivotSheet(
  sheet: WorkbookSheet | undefined,
): sheet is WorkbookPivotSheet {
  return sheet?.kind === "pivot";
}
/** Whether a Worksheet is a freeform sheet. */
export function isFreeformSheet(
  sheet: WorkbookSheet | undefined,
): sheet is WorkbookFreeformSheet {
  return sheet?.kind === "freeform";
}
/** Whether a Worksheet is columnar (Formula columns row by row). */
export function isColumnarSheet(
  sheet: WorkbookSheet | undefined,
): sheet is WorkbookColumnarSheet {
  return sheet !== undefined && (sheet.kind ?? "columnar") === "columnar";
}
/** Each Sheet kind's schema, so a Worksheet is checked against its own kind's fields. */
export const workbookSheetKindSchemas = {
  columnar: workbookColumnarSheetSchema,
  pivot: workbookPivotSheetSchema,
  freeform: workbookFreeformSheetSchema,
} satisfies Record<WorkbookSheetKind, z.ZodType>;
/** The schema of a Worksheet's own kind, read from `value`; one without a known kind is columnar. */
export function workbookSheetSchemaOf(value: unknown) {
  const kind = z
    .object({ kind: z.string().optional() })
    .loose()
    .safeParse(value).data?.kind;
  return (
    Object.entries(workbookSheetKindSchemas).find(
      ([key]) => key === kind,
    )?.[1] ?? workbookColumnarSheetSchema
  );
}

export const workbookFileSchema = z.object({
  ...item,
  fileName: z.string(),
  sheets: z.array(z.string()),
  /** When the File was uploaded. Absent on Files attached before it was recorded. */
  uploadedAt: z.iso.datetime().optional(),
});
export const workbookExportFileNameSchema = z
  .string()
  .trim()
  .min(6)
  .max(200)
  .regex(
    /^[^\\/:*?"<>|]+\.xlsx$/i,
    'Use a filename ending in .xlsx, without \\ / : * ? " < > |.',
  )
  .refine(
    (value) =>
      [...value].every(
        (character) =>
          character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127,
      ),
    "Filenames can't contain invisible characters such as tabs or line breaks.",
  );

/** The most Outputs a Workbook holds, and tabs an Output file holds. */
export const WORKBOOK_MAX_OUTPUTS = 20;
/**
 * One tab of an Output file: a Worksheet's Result, named after the Worksheet
 * unless `tabName` names it. Number formats are its Worksheet columns'.
 */
export const workbookOutputFileTabSchema = z.object({
  sheetId: z.uuid(),
  tabName: z.string().trim().min(1).max(31).optional(),
});
export type WorkbookOutputFileTab = z.infer<typeof workbookOutputFileTabSchema>;
/** The most column mappings a Data sink holds. */
export const WORKBOOK_MAX_SINK_MAPPINGS = 200;
/**
 * Where one Data sink target column's value comes from: a column of the
 * Worksheet's Result (by name), or a fixed value (null writes NULL).
 */
export const workbookSinkValueSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("column"),
    column: z.string().trim().min(1).max(200),
  }),
  z.object({
    kind: z.literal("constant"),
    value: z.string().max(1000).nullable(),
  }),
]);
export type WorkbookSinkValue = z.infer<typeof workbookSinkValueSchema>;
/** One target column of a Data sink and where its value comes from. */
export const workbookSinkMappingSchema = z.object({
  targetColumn: z.string().trim().min(1).max(63),
  value: workbookSinkValueSchema,
});
export type WorkbookSinkMapping = z.infer<typeof workbookSinkMappingSchema>;
/**
 * The table a Data sink writes:
 * - `data-source-link`: a Data Source Link's ingest table, the same table a
 *   workflow `csv_datasource_writer` lands files in, so an
 *   `observe_data_source_link` workflow picks the rows up downstream. Upserts
 *   by the table's key.
 * - `stateful-table`: a Stateful table, as the workflow `stateful_table_write`
 *   step writes it: `snapshot` replaces the table's state, `upsert` merges by
 *   its key column.
 */
export const workbookSinkTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("data-source-link"), linkId: z.uuid() }),
  z.object({
    kind: z.literal("stateful-table"),
    tableDefinitionId: z.uuid(),
    writeMode: z.enum(["snapshot", "upsert"]).default("upsert"),
  }),
]);
export type WorkbookSinkTarget = z.infer<typeof workbookSinkTargetSchema>;
const output = { id: z.uuid(), name: z.string().trim().min(1).max(200) };
/**
 * An Output: what a Workbook produces from its Worksheets' Results. It reads
 * Results and computes none, so it has no Execution root.
 * - `file`: one xlsx file whose tabs are chosen Worksheets, in order.
 * - `report`: charts laid out on the Report grid (12 columns, rows of a unit
 *   height; see `./reportCanvas.ts`), under an
 *   optional title and description; each component charts the Report dataset
 *   its `datasetKey` names (a Worksheet id), else the first Worksheet.
 * - `sink`: a Data sink, upserting one Worksheet's Result rows into a target
 *   table through column mappings. Delivered when a Run completes (if
 *   `deliverOnRun`) or on demand.
 * - `app`: an App built here from the Workbook (a `report`-profile App on this
 *   Output), linked by id. Its feeds read Worksheets' Current Results as the
 *   viewer, so it adds nothing to a Run.
 */
export const workbookOutputSchema = z.discriminatedUnion("kind", [
  z.object({
    ...output,
    kind: z.literal("file"),
    fileName: workbookExportFileNameSchema.default("export.xlsx"),
    tabs: z
      .array(workbookOutputFileTabSchema)
      .max(WORKBOOK_MAX_OUTPUTS)
      .default([]),
  }),
  z.object({
    ...output,
    kind: z.literal("report"),
    /** Shown above the grid; absent or blank = none. */
    title: z.string().max(200).optional(),
    /** Shown under the title; absent or blank = none. */
    description: z.string().max(2000).optional(),
    /**
     * The Report color palette it draws with (`reportColorPalettes.*`);
     * absent, or naming one since deleted = the organization's default.
     */
    paletteId: z.string().min(1).max(64).optional(),
    /**
     * Report filters shown above the grid, each narrowing every component
     * whose Report dataset has its column; the saved values are the defaults
     * a viewer starts from (and the PDF shows).
     */
    filters: reportFiltersSchema.optional(),
    /**
     * Normalized as it is read and written: fields an agent nested under
     * `config` sit on their component (see `normalizeReportComponent`).
     */
    layout: ReportCanvasLayoutSchema.default({ items: [] }).transform(
      normalizeReportCanvasLayout,
    ),
  }),
  z.object({
    ...output,
    kind: z.literal("sink"),
    /** The Worksheet whose Result rows it writes; absent until chosen. */
    sheetId: z.uuid().optional(),
    /** Absent until chosen. */
    target: workbookSinkTargetSchema.optional(),
    mappings: z
      .array(workbookSinkMappingSchema)
      .max(WORKBOOK_MAX_SINK_MAPPINGS)
      .default([]),
    /** Write after every successful entire Run, not only on demand. */
    deliverOnRun: z.boolean().default(false),
  }),
  z.object({
    ...output,
    kind: z.literal("app"),
    /** The App built for this Output; absent until it is built. */
    appId: z.uuid().optional(),
  }),
]);
export type WorkbookOutput = z.infer<typeof workbookOutputSchema>;
export type WorkbookOutputKind = WorkbookOutput["kind"];
export type WorkbookFileOutput = Extract<WorkbookOutput, { kind: "file" }>;
export type WorkbookReportOutput = Extract<WorkbookOutput, { kind: "report" }>;
export type WorkbookSinkOutput = Extract<WorkbookOutput, { kind: "sink" }>;
export type WorkbookAppOutput = Extract<WorkbookOutput, { kind: "app" }>;

/** A column of a Data sink target's ingest table that a mapping can write. */
export const workbookSinkTargetColumnSchema = z.object({
  name: z.string(),
  /** The Postgres type name, e.g. "text", "numeric", "date". */
  type: z.string(),
  /** Part of the table's upsert key: it must be mapped, and a row's value for it must not be empty. */
  isKey: z.boolean(),
});
export type WorkbookSinkTargetColumn = z.infer<
  typeof workbookSinkTargetColumnSchema
>;
/** What a sink target is, without how it writes: a Data Source Link or a Stateful table, by id. */
export const workbookSinkTargetRefSchema = z.object({
  kind: z.enum(["data-source-link", "stateful-table"]),
  id: z.uuid(),
});
export type WorkbookSinkTargetRef = z.infer<typeof workbookSinkTargetRefSchema>;
/**
 * A table a Data sink can write (its columns come from `sinkTargetColumns`):
 * a Data Source Link's ingest table (`schemaName.tableName`) or a Stateful
 * table (its definition name).
 */
export const workbookSinkTargetOptionSchema =
  workbookSinkTargetRefSchema.extend({
    label: z.string(),
    /** Secondary text identifying the table, e.g. `import.supplier_price_staging`. */
    detail: z.string(),
    /** A Stateful table's description, when it has one. */
    description: z.string().optional(),
    /**
     * A Stateful table's columns, read with the table; a Link's come only from
     * `sinkTargetColumns`, since reading them queries its ingest table.
     */
    columns: z.array(workbookSinkTargetColumnSchema).optional(),
  });
export type WorkbookSinkTargetOption = z.infer<
  typeof workbookSinkTargetOptionSchema
>;
/** Why a Data sink can't deliver, or a row it couldn't write. */
export const workbookSinkIssueSchema = z.object({
  code: z.enum([
    "no-worksheet",
    "no-target",
    "no-mappings",
    "no-result",
    "target-missing",
    "unknown-target-column",
    "unknown-source-column",
    "duplicate-target-column",
    "key-column-unmapped",
    "rows-failed",
    "target-write-failed",
    "too-large",
  ]),
  message: z.string(),
  /** The target column the issue is about, when it is about one. */
  column: z.string().optional(),
});
export type WorkbookSinkIssue = z.infer<typeof workbookSinkIssueSchema>;
/**
 * What delivering a Data sink did. With any non-"rows-failed" issue nothing was
 * written; `rowCount` rows were upserted and `failedCount` rejected otherwise.
 */
export const workbookSinkDeliverySchema = z.object({
  rowCount: z.number().int(),
  failedCount: z.number().int(),
  issues: z.array(workbookSinkIssueSchema),
});
export type WorkbookSinkDelivery = z.infer<typeof workbookSinkDeliverySchema>;

export const WORKBOOK_MAX_PROCESS_STEPS = 100;

const EXECUTION_ROOT_PREFIX = "execution:";
/** The Outline id of the Execution root standing for a Worksheet. */
export function executionRootId(itemId: string): string {
  return `${EXECUTION_ROOT_PREFIX}${itemId}`;
}
/** The Item an Execution root stands for, or null when `id` is not a root id. */
export function executionRootItemId(id: string): string | null {
  return id.startsWith(EXECUTION_ROOT_PREFIX)
    ? id.slice(EXECUTION_ROOT_PREFIX.length)
    : null;
}

const workbookProcessStepSchema = z.object({
  id: z.string().min(1).max(200),
  instructions: z.string().max(10_000),
  title: z.string().trim().max(200).optional(),
  reviewStatus: z
    .enum(["instruction-edited", "configuration-edited", "both-edited"])
    .optional(),
  /**
   * Where the text came from when the user did not type it: an accepted SOP
   * update, rewritten by the AI from the Revision it names. Dropped when the
   * user next edits the text.
   */
  provenance: z
    .object({
      kind: z.literal("ai-updated"),
      revision: z.number().int().nonnegative(),
      updateId: z.uuid(),
    })
    .optional(),
  /** Omitted means: under the first linked Worksheet's Execution root, else none. */
  parentId: z.string().min(1).max(200).nullable().optional(),
  itemIds: z.array(z.string().min(1).max(200)).max(41).default([]),
});
type ProcessStepInput = z.output<typeof workbookProcessStepSchema>;
type ProcessItems = {
  sources: readonly { id: string }[];
  sheets: readonly { id: string }[];
};

/** Only Worksheets have an Execution root; Sources, Files and Outputs have none. */
function executionRootIds(definition: ProcessItems): Set<string> {
  return new Set(definition.sheets.map((sheet) => executionRootId(sheet.id)));
}

/**
 * The Source a stored Parent names when it is a Source's Execution root, which
 * Definitions saved before Sources lost theirs may hold. Parsing reads it as an
 * omitted Parent with that Source linked.
 */
function legacySourceRootId(
  definition: ProcessItems,
  parentId: string | null | undefined,
): string | null {
  const itemId = parentId ? executionRootItemId(parentId) : null;
  return itemId && definition.sources.some((source) => source.id === itemId)
    ? itemId
    : null;
}

/**
 * Why the Process's Parents are invalid: each Parent must be an Execution root or
 * another step, and following Parents must never return to a step.
 */
function processParentIssues(
  definition: ProcessItems & { processSteps: readonly ProcessStepInput[] },
): string[] {
  const issues = new Set<string>();
  const roots = executionRootIds(definition);
  const parents = new Map(
    definition.processSteps.map((step) => [step.id, step.parentId]),
  );
  for (const step of definition.processSteps) {
    if (
      roots.has(step.id) ||
      danglingParent(definition, roots, parents, step.parentId)
    )
      issues.add("Process parents must reference an existing step.");
    if (reachesItself(step, parents))
      issues.add("Process steps cannot contain a cycle.");
  }
  return [...issues];
}

/** A Parent that is neither an Execution root, a step, nor a legacy Source root. */
function danglingParent(
  definition: ProcessItems,
  roots: ReadonlySet<string>,
  parents: ReadonlyMap<string, string | null | undefined>,
  parentId: string | null | undefined,
): boolean {
  return (
    !!parentId &&
    !roots.has(parentId) &&
    !parents.has(parentId) &&
    !legacySourceRootId(definition, parentId)
  );
}

/** Whether following Parents from a step returns to a step already passed. */
function reachesItself(
  step: Pick<ProcessStepInput, "id" | "parentId">,
  parents: ReadonlyMap<string, string | null | undefined>,
): boolean {
  const ancestors = new Set([step.id]);
  let parent = step.parentId;
  while (parent && parents.has(parent)) {
    if (ancestors.has(parent)) return true;
    ancestors.add(parent);
    parent = parents.get(parent);
  }
  return false;
}

/**
 * Fills in each omitted Parent, so every parsed step has an explicit one: the
 * first linked Worksheet's Execution root, else null. A legacy Source-root
 * Parent is filled in the same way with that Source linked, so an Instruction
 * that only describes data becomes a Data note on it.
 */
function withProcessParents(
  definition: ProcessItems & { processSteps: readonly ProcessStepInput[] },
) {
  const roots = executionRootIds(definition);
  return definition.processSteps.map((step) => {
    const legacySourceId = legacySourceRootId(definition, step.parentId);
    const itemIds =
      legacySourceId && !step.itemIds.includes(legacySourceId)
        ? [...step.itemIds, legacySourceId]
        : step.itemIds;
    return {
      ...step,
      itemIds,
      parentId:
        step.parentId === undefined || legacySourceId
          ? (itemIds.map(executionRootId).find((id) => roots.has(id)) ?? null)
          : step.parentId,
    };
  });
}

/**
 * The Output Definitions saved before Outputs were a list held: one Worksheet
 * (`outputSheetId`) exported as `exportFileName`, and one Report (`report`).
 * The single Output's Item id and Execution root were `outputs`.
 */
const LEGACY_OUTPUT_ITEM_ID = "outputs";
/** The ids parsing gives the legacy Output and Report; any uuid unique in the Definition would do. */
const LEGACY_FILE_OUTPUT_ID = "0f6c1d3e-5a7b-4c8d-9e0f-1a2b3c4d5e6f";
const LEGACY_REPORT_OUTPUT_ID = "1a7d2e4f-6b8c-4d9e-8f1a-2b3c4d5e6f70";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

/**
 * A legacy Report's layout for its Report Output: a component charting the
 * Output (`outputs`), or naming no dataset while the Output was its default,
 * charts the Output's Worksheet.
 */
function legacyReportLayout(
  layout: Record<string, unknown>,
  outputSheetId: string | null,
): Record<string, unknown> {
  const component = (value: unknown) => {
    if (!isRecord(value)) return value;
    const { datasetKey, ...rest } = value;
    const key =
      datasetKey === undefined || datasetKey === LEGACY_OUTPUT_ITEM_ID
        ? outputSheetId
        : datasetKey;
    return key === null ? rest : { ...rest, datasetKey: key };
  };
  if (Array.isArray(layout.items))
    return {
      ...layout,
      items: layout.items.map((item: unknown) =>
        isRecord(item)
          ? { ...item, component: component(item.component) }
          : item,
      ),
    };
  return {
    ...layout,
    data: Array.isArray(layout.data)
      ? layout.data.map((row) =>
          Array.isArray(row) ? row.map(component) : row,
        )
      : layout.data,
  };
}

/**
 * A legacy Instruction as the Outputs list reads it: a Parent of the Output's
 * Execution root becomes its Worksheet's (or none), and a link or Reference to
 * the Output names the File Output that replaced it (or is dropped).
 */
function legacyProcessStep(
  step: Record<string, unknown>,
  outputSheetId: string | null,
): Record<string, unknown> {
  const fileId = outputSheetId ? LEGACY_FILE_OUTPUT_ID : null;
  const parentId =
    step.parentId === executionRootId(LEGACY_OUTPUT_ITEM_ID)
      ? outputSheetId && executionRootId(outputSheetId)
      : step.parentId;
  const itemIds = Array.isArray(step.itemIds)
    ? step.itemIds.flatMap((id: unknown) =>
        id !== LEGACY_OUTPUT_ITEM_ID ? [id] : fileId ? [fileId] : [],
      )
    : step.itemIds;
  const instructions =
    typeof step.instructions === "string" && fileId
      ? step.instructions
          .split(`{workbook:${LEGACY_OUTPUT_ITEM_ID}}`)
          .join(`{workbook:${fileId}}`)
      : step.instructions;
  return { ...step, parentId, itemIds, instructions };
}

/**
 * Reads a Definition saved before Outputs were a list, without a migration:
 * `outputSheetId` with `exportFileName` becomes one File Output with that
 * Worksheet as its tab, named after it, and `report` one Report Output. A
 * Definition that has `outputs` is read as it is.
 */
function withLegacyOutputs(value: unknown): unknown {
  if (!isRecord(value)) return value;
  const { outputSheetId, exportFileName, report, ...definition } = value;
  if ("outputs" in definition) return definition;
  const sheetId = typeof outputSheetId === "string" ? outputSheetId : null;
  const sheet = records(definition.sheets).find((item) => item.id === sheetId);
  const outputs: Record<string, unknown>[] = [];
  if (sheetId)
    outputs.push({
      id: LEGACY_FILE_OUTPUT_ID,
      name: typeof sheet?.name === "string" ? sheet.name : "Output",
      kind: "file",
      fileName: exportFileName,
      tabs: [{ sheetId }],
    });
  if (isRecord(report))
    outputs.push({
      id: LEGACY_REPORT_OUTPUT_ID,
      name: "Report",
      kind: "report",
      layout: legacyReportLayout(report, sheetId),
    });
  return {
    ...definition,
    outputs,
    processSteps: Array.isArray(definition.processSteps)
      ? definition.processSteps.map((step: unknown) =>
          isRecord(step) ? legacyProcessStep(step, sheetId) : step,
        )
      : definition.processSteps,
  };
}

export const WORKBOOK_MAX_TASK_RULES = 20;
/** The most Tasks one Task rule opens for one Run: the Preview bound. */
export const WORKBOOK_MAX_RULE_TASKS = WORKBOOK_PREVIEW_ROWS;
/**
 * A Task rule: after each completed Run that computed its Worksheet, open a
 * Task per row of that Worksheet's Result (an exceptions Worksheet: rows that
 * need a person), at most `WORKBOOK_MAX_RULE_TASKS`. Presentational: never
 * Compute-relevant. A rule whose Worksheet was removed does nothing.
 */
export const workbookTaskRuleSchema = z.object({
  id: z.uuid(),
  sheetId: z.uuid(),
  /** The column whose value titles each Task; the Worksheet's first column when absent. */
  titleColumn: z.string().min(1).max(200).optional(),
  assigneeUserId: z.uuid().nullable().default(null),
  priority: z.enum(["low", "medium", "high", "urgent"]).default("medium"),
});
export type WorkbookTaskRule = z.infer<typeof workbookTaskRuleSchema>;

const workbookDefinitionShape = z.object({
  formatVersion: z.literal(1),
  name: z.string().trim().min(1).max(200),
  instructions: z.string().max(30_000).default(""),
  /** Apply SOP updates as they arrive instead of proposing them; each stays undoable. */
  sopAutoApply: z.boolean().optional(),
  processSteps: z
    .array(workbookProcessStepSchema)
    .max(WORKBOOK_MAX_PROCESS_STEPS)
    .default([]),
  files: z.array(workbookFileSchema).max(20).default([]),
  sources: z.array(workbookSourceSchema).max(20).default([]),
  sheets: z.array(workbookSheetSchema).max(20).default([]),
  /** What the Workbook produces: Files and Reports, in navigation order. */
  outputs: z.array(workbookOutputSchema).max(WORKBOOK_MAX_OUTPUTS).default([]),
  /** Tasks integration: Task rules applied when a Run completes. */
  taskRules: z
    .array(workbookTaskRuleSchema)
    .max(WORKBOOK_MAX_TASK_RULES)
    .default([]),
  /** How a Sampled run picks its subset; absent until one is chosen. Presentational. */
  sampling: workbookSamplingSchema.optional(),
});
/** A Definition as written: Parents may be omitted. */
export type WorkbookDefinitionInput = z.input<typeof workbookDefinitionShape>;

function sinkIssues(
  output: WorkbookSinkOutput,
  sheetIds: ReadonlySet<string>,
): string[] {
  const issues: string[] = [];
  if (output.sheetId !== undefined && !sheetIds.has(output.sheetId))
    issues.push("A data sink names a worksheet that does not exist.");
  const targets = output.mappings.map((m) => m.targetColumn);
  if (new Set(targets).size !== targets.length)
    issues.push("A data sink maps a target column more than once.");
  return issues;
}

function outputIssues(
  definition: z.output<typeof workbookDefinitionShape>,
): string[] {
  const sheetIds = new Set(definition.sheets.map((sheet) => sheet.id));
  const issues = new Set<string>();
  for (const output of definition.outputs) {
    if (output.kind === "sink") {
      for (const issue of sinkIssues(output, sheetIds)) issues.add(issue);
      continue;
    }
    if (output.kind !== "file") continue;
    const tabs = output.tabs.map((tab) => tab.sheetId);
    if (tabs.some((id) => !sheetIds.has(id)))
      issues.add("An output file's tab names a worksheet that does not exist.");
    if (new Set(tabs).size !== tabs.length)
      issues.add("A worksheet can be only one tab of an output file.");
  }
  return [...issues];
}

export const workbookDefinitionSchema = z
  .preprocess(
    (value: WorkbookDefinitionInput) => withLegacyOutputs(value),
    workbookDefinitionShape,
  )
  .superRefine((definition, context) => {
    const ids = [
      ...definition.files,
      ...definition.sources,
      ...definition.sheets,
      ...definition.outputs,
    ].map((entry) => entry.id);
    const stepIds = definition.processSteps.map((step) => step.id);
    if (new Set(stepIds).size !== stepIds.length)
      context.addIssue({
        code: "custom",
        message: "Process step IDs must be unique.",
      });
    const processItemIds = new Set(ids);
    for (const step of definition.processSteps) {
      if (step.itemIds.some((id) => !processItemIds.has(id)))
        context.addIssue({
          code: "custom",
          message: "A process step references an unknown workbook item.",
        });
    }
    for (const message of processParentIssues(definition))
      context.addIssue({ code: "custom", message });
    if (new Set(ids).size !== ids.length)
      context.addIssue({
        code: "custom",
        message: "Workbook item IDs must be unique.",
      });
    for (const message of outputIssues(definition))
      context.addIssue({ code: "custom", message });
  })
  .transform((definition) => ({
    ...definition,
    processSteps: withProcessParents(definition),
  }));
export type WorkbookDefinition = z.infer<typeof workbookDefinitionSchema>;

/**
 * Filter counts: the rows entering a Worksheet's filters (after Lookups and
 * Formulas) and, per filter in order, the rows it removed. A row counts against
 * the first filter it fails, so the removals sum to the rows filtered out.
 */
export const workbookFilterCountsSchema = z.object({
  inputRowCount: z.number().int().nonnegative(),
  filters: z
    .array(
      z.object({
        /** The filter as the Snapshot ran it, so counts stay attributable after edits. */
        filter: workbookFilterSchema,
        removedRowCount: z.number().int().nonnegative(),
      }),
    )
    .max(30),
});
export type WorkbookFilterCounts = z.infer<typeof workbookFilterCountsSchema>;
/** One Input-order preview row: output cells before filters and sort, and the index in `filterCounts.filters` of the filter that removed it, or null when kept. */
export const workbookInputOrderRowSchema = z.object({
  cells: z.array(workbookCellSchema),
  removedBy: z.number().int().nonnegative().nullable(),
  /**
   * The row's Sort position: its zero-based place among all staged rows in the
   * Worksheet's sort order, ignoring filters. Absent on Results from Runs before it existed.
   */
  sortPosition: z.number().int().nonnegative().optional(),
});
export type WorkbookInputOrderRow = z.infer<typeof workbookInputOrderRowSchema>;
/**
 * One Field's Distinct values: its values in a Run's staged rows (before
 * filters), read as text, with how many rows hold each, plus how many rows are
 * Empty or Error cells. Lists the most frequent WORKBOOK_MAX_DISTINCT_VALUES,
 * in the Field's sort order.
 */
export const workbookDistinctValuesSchema = z.object({
  column: z.string(),
  values: z
    .array(
      z.object({
        value: z.string(),
        count: z.number().int().positive(),
      }),
    )
    .max(WORKBOOK_MAX_DISTINCT_VALUES),
  emptyCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  /** The Field has values `values` leaves out. */
  truncated: z.boolean(),
});
export type WorkbookDistinctValues = z.infer<
  typeof workbookDistinctValuesSchema
>;

/**
 * How one Validation fared in a Run: the rule as the Run checked it, how many
 * of the Result's rows failed it, and the first of them (their positions in
 * the Result and their cells), to show and to filter the grid to.
 */
export const workbookValidationResultSchema = z.object({
  validation: workbookValidationSchema,
  failedRowCount: z.number().int().nonnegative(),
  failedRows: z
    .array(
      z.object({
        row: z.number().int().nonnegative(),
        cells: z.array(workbookCellSchema),
      }),
    )
    .max(WORKBOOK_VALIDATION_SAMPLE_ROWS),
});
export type WorkbookValidationResult = z.infer<
  typeof workbookValidationResultSchema
>;
export const workbookResultSchema = z.object({
  sheetId: z.uuid(),
  columns: z.array(z.string()),
  /** The preview: the Result's first rows, or for a Large Result its Sample. */
  rows: z.array(z.array(workbookCellSchema)).max(WORKBOOK_PREVIEW_ROWS),
  rowCount: z.number().int().nonnegative(),
  /**
   * A Large Result's preview is a Sample: how it was taken, and each row's
   * position in the Result. Absent when `rows` are the Result's first rows.
   */
  sample: z
    .object({
      sample: workbookSampleSchema,
      rowNumbers: z
        .array(z.number().int().nonnegative())
        .max(WORKBOOK_PREVIEW_ROWS),
    })
    .optional(),
  /** Each of the Worksheet's Validations as the Run checked it; absent before Validations existed. */
  validations: z.array(workbookValidationResultSchema).optional(),
  /** Error cells in the whole Result. Absent on Results from Runs before Error cells existed. */
  errorCount: z.number().int().nonnegative().optional(),
  /** Absent on Results from Runs before Filter counts existed. */
  filterCounts: workbookFilterCountsSchema.optional(),
  /** The Input-order preview; absent on Results from Runs before it existed. */
  inputOrderRows: z
    .array(workbookInputOrderRowSchema)
    .max(WORKBOOK_PREVIEW_ROWS)
    .optional(),
  /** Distinct values of every Field filters can read; absent on Results from Runs before they existed. */
  distinctValues: z.array(workbookDistinctValuesSchema).optional(),
  /**
   * A pivot's Column keys in its input after its Filters, in the Column
   * fields' sort order, pinned or not: what Pin columns pins. Absent on
   * other Worksheets.
   */
  pivotColumnKeys: z
    .array(z.array(z.string()))
    .max(WORKBOOK_PIVOT_MAX_COLUMN_KEYS * 5)
    .optional(),
  /**
   * Each column's number format as the Run computed it (null: none), by
   * position: a pivot's shown-as Values are percentages. It keeps a Stale
   * Result showing as it was computed after its spec changes. Absent on
   * columnar Worksheets, whose formats are presentational.
   */
  columnFormats: z.array(z.string().nullable()).optional(),
  /**
   * A freeform sheet's computed grid: every filled cell's value, by A1
   * address. Absent on other Worksheets.
   */
  freeformCells: z
    .record(workbookCellAddressSchema, workbookCellSchema)
    .optional(),
  /** Each Number column's total over the whole Result; absent on Runs before totals were recorded. */
  totals: workbookResultTotalsSchema.optional(),
  /** The Worksheet's Fingerprint in the Run's Snapshot; the Result is Current while the edited Definition matches it. */
  fingerprint: z.string(),
});
/** A Sample of one completed Run's Result for a Worksheet, taken on request. */
export const workbookResultSampleInputSchema = z.object({
  workbookId: z.uuid(),
  runId: z.uuid(),
  sheetId: z.uuid(),
  sample: workbookSampleSchema,
});
/**
 * A Result's Sample: how it was taken, its rows in the Result's order with
 * their positions, and the Result's true row count.
 */
export const workbookResultSampleSchema = z.object({
  sample: workbookSampleSchema,
  rowNumbers: z.array(z.number().int().nonnegative()),
  rows: z.array(z.array(workbookCellSchema)),
  rowCount: z.number().int().nonnegative(),
});
export type WorkbookResultSample = z.infer<typeof workbookResultSampleSchema>;
/** The most rows a Source sample (Add data's preview) shows. */
export const WORKBOOK_SOURCE_SAMPLE_MAX_ROWS = 1_000;
/** How many of a Source's leading rows a Source sample is taken from, bounding the read. */
export const WORKBOOK_SOURCE_SAMPLE_SCAN_ROWS = 20_000;
export const workbookSourceSampleInputSchema = z.object({
  workbookId: z.uuid(),
  source: workbookSourceSchema,
  sample: workbookSampleSchema,
  rows: z.number().int().min(1).max(WORKBOOK_SOURCE_SAMPLE_MAX_ROWS),
});
/**
 * A Sample of a Source before it is added: its rows in the Source's order with
 * their positions, taken from its first `scannedRows` rows (all of them when
 * `scannedAll`), long cells cut as a preview cuts them.
 */
export const workbookSourceSampleSchema = workbookTableSchema.safeExtend({
  rowNumbers: z.array(z.number().int().nonnegative()),
  scannedRows: z.number().int().nonnegative(),
  scannedAll: z.boolean(),
});
export type WorkbookSourceSample = z.infer<typeof workbookSourceSampleSchema>;
/** `queued → running → completed | failed`; completed and failed are terminal. */
/**
 * The parts of a Run's Results the Run list leaves out — each Worksheet's
 * Input-order preview and Distinct values — read once per Run.
 */
export const workbookRunDetailsSchema = z.array(
  workbookResultSchema.pick({
    sheetId: true,
    inputOrderRows: true,
    distinctValues: true,
  }),
);
export type WorkbookRunDetails = z.infer<typeof workbookRunDetailsSchema>;
export const workbookRunStatusSchema = z.enum([
  "queued",
  "running",
  "completed",
  "failed",
]);
export type WorkbookRunStatus = z.infer<typeof workbookRunStatusSchema>;
/**
 * Run mode: `entire` runs every Worksheet on a fresh Snapshot of every Source;
 * `up-to` runs the target Worksheet and its upstream closure on a fresh Snapshot
 * of the Sources they read; `from` runs the target and everything downstream,
 * reusing a Base run's Snapshot and upstream Results; `only` runs the target
 * alone on the same Base run's inputs, leaving downstream Results as they were;
 * `sample` runs every Worksheet over a join-coherent subset of the Sources (the Definition's
 * `sampling`), and its Results are never exported, built on or acted on.
 */
export const workbookRunModeSchema = z.enum([
  "entire",
  "up-to",
  "from",
  "only",
  "sample",
]);
export type WorkbookRunMode = z.infer<typeof workbookRunModeSchema>;
/**
 * Where a Run came from: a person in the editor (the on-page agent included),
 * a Run Workbook workflow node, or an agent tool. Only a person's Run publishes
 * the run-completed event: a Run an automation started reports back to it
 * directly, so an automation reacting to its Workbook's completion can't
 * trigger itself forever.
 */
export const workbookRunOriginSchema = z.enum(["editor", "workflow", "agent"]);
export type WorkbookRunOrigin = z.infer<typeof workbookRunOriginSchema>;
/**
 * A Source a Run's Snapshot holds, with its Fingerprint at Run start. The
 * Fingerprint folds in the Data version the Run read, when it recorded one.
 */
export const workbookRunSourceSchema = z.object({
  sourceId: z.uuid(),
  fingerprint: z.string(),
  /**
   * The Source's Data version when the Run read it; null when the Source has no
   * cheap change marker, so its data can never be proven unchanged. Absent on
   * Runs recorded before Data versions existed.
   */
  dataVersion: z.string().nullable().optional(),
});
export type WorkbookRunSource = z.infer<typeof workbookRunSourceSchema>;
/**
 * Where a Run's time goes: the step it is on now, and how long each Worksheet
 * it computed took. Recorded as the Run executes; null on a queued Run and on
 * Runs from before timing existed.
 */
export const workbookRunTimingSchema = z.object({
  /** When the Run began executing, after any wait in the queue. */
  startedAt: z.string().optional(),
  /** The step under way and when it started; null once the Run finished. */
  step: z
    .object({
      /** Reading the Sources, computing a Worksheet, or checking and storing Results. */
      kind: z.enum(["sources", "sheet", "saving"]),
      /** The Worksheet a `sheet` step computes. */
      sheetId: z.string().optional(),
      startedAt: z.string(),
    })
    .nullable(),
  /** Each computed Worksheet's time, in the order they finished. */
  sheets: z.array(
    z.object({ sheetId: z.string(), durationMs: z.number().nonnegative() }),
  ),
});
export type WorkbookRunTiming = z.infer<typeof workbookRunTimingSchema>;
export const workbookRunSchema = z.object({
  id: z.uuid(),
  workbookId: z.uuid(),
  revision: z.number().int(),
  status: workbookRunStatusSchema,
  mode: workbookRunModeSchema,
  /** The Target: the Worksheet an `up-to` or `from` Run, or the Output an `up-to` Run, was started on. */
  targetId: z.uuid().nullable(),
  /** The Run whose Snapshot and upstream Results a `from` Run reused. */
  baseRunId: z.uuid().nullable(),
  sources: z.array(workbookRunSourceSchema),
  createdAt: z.string(),
  error: z.string().nullable(),
  results: z.array(workbookResultSchema),
  workflowRunId: z.uuid().nullable(),
  origin: workbookRunOriginSchema.default("editor"),
  triggeredBy: z
    .object({ id: z.uuid(), name: z.string() })
    .nullable()
    .default(null),
  /** A Sampled run's sample; null for every other mode. */
  sampling: workbookRunSamplingSchema.nullable().default(null),
  /** Drift warnings: this Run against earlier ones, recorded when it completed. */
  comparisons: z.array(workbookRunComparisonSchema).default([]),
  timing: workbookRunTimingSchema.nullable().default(null),
});

export const workbookInspectionSchema = z.object({
  sheets: z.array(
    z.object({
      name: z.string(),
      rowCount: z.number(),
      columnCount: z.number(),
    }),
  ),
  sheetName: z.string(),
  startRow: z.number(),
  truncated: z.boolean(),
  rows: z.array(
    z.array(
      z.object({
        address: z.string(),
        text: z.string(),
        formula: z.string().nullable(),
        numberFormat: z.string().nullable(),
        value: workbookCellSchema,
      }),
    ),
  ),
});

export type WorkbookResult = z.infer<typeof workbookResultSchema>;
export type WorkbookRun = z.infer<typeof workbookRunSchema>;
